import EmailIcon from '@mui/icons-material/MailOutline';
import WhatsappIcon from '@mui/icons-material/WhatsApp';
import { Box, Button, Checkbox, FormControlLabel, Grid, Typography } from '@mui/material';
import { isAxiosError } from 'axios';
import { FC, useEffect, useState } from 'react';
import {
  Contact,
  VerifyOtpError,
  VerifyOtpForIdentityCheckFlowRes,
  VerifyOtpReqError,
  VerifyOtpRes,
} from '~/api/types';
import { MuiOtpInput } from '~/components/ui/otp-input';
import { Page } from '~/components/ui/page';
import { Body1, Title } from '~/components/ui/typography';
import { colors } from '~/theme';
import { LoadingScreen } from './loading';
import { ErrorScreen } from './error';

export const OtpVerificationScreen: FC<OtpVerificationScreenProps> = ({
  contact,
  otpId,
  onOtpConfirmation,
  resendOtp,
  verifyOtp,
  showRememberMeOption,
  buttonText,
  title = 'Validação de segurança',
  description = 'Por favor, digite o código de verificação para acessar sua área do cliente',
}) => {
  const [verifyOtpReqStatus, setVerifyOtpReqStatus] = useState<VerifyOtpReqStatus>('idle');
  const [resendOtpReqStatus, setResendOtpReqStatus] = useState<ResendOtpReqStatus>('idle');
  const [timer, setTimer] = useState(60);
  const [otp, setOtp] = useState('');
  const [rememberMe, setRememberMe] = useState(true);

  const verifyOtpReqErrorMessage = isVerifyOtpReqError(verifyOtpReqStatus)
    ? getErrorMessageFromVerifyOtpReqStatus(verifyOtpReqStatus)
    : '';
  const resendOtpReqErrorMessage = isResendOtpReqError(resendOtpReqStatus)
    ? getErrorMessageFromResendOtpReqStatus(resendOtpReqStatus)
    : null;

  useEffect(() => {
    if (timer > 0) setTimeout(() => setTimer(timer - 1), 1000);
  }, [timer]);

  const onConfirm = async (token: string) => {
    if (token.length < 6) return;
    setVerifyOtpReqStatus('in-progress');
    try {
      const res = await verifyOtp(otpId, token, rememberMe);
      onOtpConfirmation(res);
    } catch (e) {
      console.error(e);
      const error = isAxiosError<VerifyOtpError>(e) && e.response?.data?.error ? e.response.data.error : 'error';
      setOtp('');
      setVerifyOtpReqStatus(error);
    }
  };

  const resendToken = async () => {
    setResendOtpReqStatus('in-progress');
    try {
      await resendOtp(otpId);
      setResendOtpReqStatus('idle');
      setTimer(60);
    } catch (error: unknown) {
      console.error(error);
      if (isAxiosError(error) && error.response?.status === 429) {
        setResendOtpReqStatus('too-many-requests');
      } else {
        setResendOtpReqStatus('error');
      }
    }
  };

  if (resendOtpReqStatus === 'in-progress') {
    return <LoadingScreen message="Enviando acesso" />;
  }

  if (verifyOtpReqStatus === 'in-progress') {
    return <LoadingScreen message="Validando token..." />;
  }

  if (resendOtpReqErrorMessage) {
    return (
      <ErrorScreen
        title={resendOtpReqErrorMessage.title}
        message={resendOtpReqErrorMessage.message}
        refetch={resendToken}
        cancel={() => setResendOtpReqStatus('idle')}
      />
    );
  }

  const Icon = contact.type === 'email' ? EmailIcon : WhatsappIcon;

  return (
    <Page innerWrapperSx={{ width: { xs: '100%', sm: '800px' } }}>
      <Title>{title}</Title>
      <Box sx={{ my: 4, p: 2, background: colors.lightGray, borderRadius: '4px' }}>
        <Typography variant="body2" color={colors.darkGray}>
          Verifique a mensagem que enviamos para:
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', my: 1 }}>
          <Icon sx={{ color: colors.darkGray }} />
          <Typography sx={{ ml: 1, fontSize: { xs: 12, md: 14 }, wordWrap: 'break-word', overflow: 'hidden' }}>
            {contact.maskedValue}
          </Typography>
        </Box>
      </Box>
      <Body1>{description}</Body1>
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Box sx={{ maxWidth: '450px', textAlign: 'left' }}>
          <MuiOtpInput
            autoFocus
            length={6}
            value={otp}
            onChange={(e) => setOtp(e)}
            validateChar={(char) => /[0-9]/.test(char)}
            onComplete={onConfirm}
          />
          {showRememberMeOption && (
            <FormControlLabel
              sx={{ mt: { xs: 1, md: 0 } }}
              control={<Checkbox checked={rememberMe} />}
              label="Mantenha-me conectado"
              value={rememberMe}
              onChange={() => setRememberMe((prev) => !prev)}
            />
          )}
        </Box>
      </Box>
      {verifyOtpReqErrorMessage && <Typography color="red">{verifyOtpReqErrorMessage}</Typography>}
      <Grid container>
        <Grid item xs={12} sm={6} mt={3}>
          {isOtpBlocked(verifyOtpReqStatus) ? (
            <Button onClick={() => window.location.reload()} sx={{ fontWeight: 'bold', textTransform: 'none' }}>
              SOLICITAR NOVO TOKEN
            </Button>
          ) : (
            <Button disabled={timer > 0} onClick={resendToken} sx={{ fontWeight: 'bold', textTransform: 'none' }}>
              REENVIAR TOKEN {timer > 0 ? `(em ${timer}s)` : ''}
            </Button>
          )}
        </Grid>
        <Grid item xs={12} sm={6} mt={3}>
          <Button
            disabled={otp.length < 6}
            onClick={() => onConfirm(otp)}
            fullWidth
            variant="contained"
            sx={{ fontWeight: 'bold', fontSize: '14px' }}
          >
            {buttonText}
          </Button>
        </Grid>
      </Grid>
    </Page>
  );
};

interface OtpVerificationScreenProps {
  contact: Contact;
  otpId: string;
  onOtpConfirmation: (arg: VerifyOtpForIdentityCheckFlowRes | VerifyOtpRes) => void;
  verifyOtp: (
    otpId: string,
    token: string,
    rememberMe: boolean,
  ) => Promise<VerifyOtpForIdentityCheckFlowRes | VerifyOtpRes>;
  resendOtp: (otpId: string) => Promise<void>;
  showRememberMeOption?: boolean;
  buttonText: string;
  title?: string;
  description?: string;
}

type VerifyOtpReqStatus = 'idle' | 'in-progress' | VerifyOtpReqError;
function isVerifyOtpReqError(reqStatus: VerifyOtpReqStatus): reqStatus is VerifyOtpReqError {
  return ['expired-token', 'too-many-attempts', 'invalid-token', 'notification-opt-in-failed', 'error'].includes(
    reqStatus,
  );
}

function isOtpBlocked(reqStatus: VerifyOtpReqStatus): boolean {
  return ['expired-token', 'too-many-attempts'].includes(reqStatus);
}

function getErrorMessageFromVerifyOtpReqStatus(reqStatus: VerifyOtpReqError): string {
  const errorMap: Record<VerifyOtpReqError, string> = {
    'expired-token': 'Token expirado. Por favor, solicite um novo acesso.',
    'too-many-attempts': 'Muitas tentativas. Por favor, solicite um novo acesso.',
    'invalid-token': 'Token inválido',
    'notification-opt-in-failed':
      'Não foi possível ativar o envio dos boletos de todos os empreendimentos selecionados. Tente novamente.',
    'error': 'Erro desconhecido. Tente novamente mais tarde',
  };
  return errorMap[reqStatus];
}

type ResendOtpReqStatus = 'idle' | 'in-progress' | ResendOtpReqError;
type ResendOtpReqError = 'too-many-requests' | 'error';
function isResendOtpReqError(reqStatus: ResendOtpReqStatus): reqStatus is ResendOtpReqError {
  return ['too-many-requests', 'error'].includes(reqStatus);
}

function getErrorMessageFromResendOtpReqStatus(reqStatus: ResendOtpReqError): ErrorDesciption {
  const errorMap: Record<ResendOtpReqError, ErrorDesciption> = {
    'too-many-requests': {
      title: 'Muitas tentativas',
      message: 'Você atingiu o limite de envios. Tente novamente mais tarde.',
    },
    'error': {
      title: 'Erro de envio',
      message: 'Houve um problema para enviar seu acesso.\nPor favor, tente novamente.',
    },
  };
  return errorMap[reqStatus];
}

interface ErrorDesciption {
  title: string;
  message: string;
}
